"use client";

import * as React from "react";

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useHistoryManager } from "@/hooks/use-history-manager";
import { useMobile } from "@/hooks/use-mobile";
import { cn } from "@/utils/lib";

type PopoverDrawerType = "drawer" | "popover";

type PopoverDrawerContextValue = {
  close: () => void;
  open: boolean;
  title: string;
  type: PopoverDrawerType;
};

const PopoverDrawerContext =
  React.createContext<PopoverDrawerContextValue | null>(null);

export const usePopoverDrawerContext = () => {
  const context = React.useContext(PopoverDrawerContext);

  if (!context) {
    throw new Error(
      "usePopoverDrawerContext must be used within PopoverDrawer",
    );
  }

  return context;
};

export type PopoverDrawerProps = {
  children: React.ReactNode;
  defaultOpen?: boolean;
  drawerProps?: React.ComponentProps<typeof Drawer>;
  historyName?: string;
  historyReplace?: boolean;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  popoverProps?: React.ComponentProps<typeof Popover>;
  title: string;
};

export const PopoverDrawer = ({
  children,
  defaultOpen = false,
  drawerProps,
  historyName,
  historyReplace = false,
  onOpenChange,
  open: openProp,
  popoverProps,
  title,
}: PopoverDrawerProps) => {
  const { isMobile } = useMobile();
  const [hasMounted, setHasMounted] = React.useState(false);
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;

  React.useEffect(() => {
    setHasMounted(true);
  }, []);

  const isDrawer = hasMounted && isMobile;

  const setOpen = React.useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(nextOpen);
      }

      onOpenChange?.(nextOpen);
    },
    [isControlled, onOpenChange],
  );

  const history = useHistoryManager({
    enabled: isDrawer,
    kind: "popover-drawer",
    name: historyName ?? title,
    onOpenChange: setOpen,
    open,
    replace: historyReplace,
  });
  const historyOnOpenChange = history.onOpenChange;

  const rootOnOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (isDrawer) {
        historyOnOpenChange(nextOpen);
        return;
      }

      setOpen(nextOpen);
    },
    [historyOnOpenChange, isDrawer, setOpen],
  );

  const close = React.useCallback(() => {
    rootOnOpenChange(false);
  }, [rootOnOpenChange]);

  const context = React.useMemo<PopoverDrawerContextValue>(
    () => ({
      close,
      open,
      title,
      type: isDrawer ? "drawer" : "popover",
    }),
    [close, isDrawer, open, title],
  );

  const content = (
    <PopoverDrawerContext.Provider value={context}>
      {children}
    </PopoverDrawerContext.Provider>
  );

  if (isDrawer) {
    return (
      <Drawer open={open} onOpenChange={rootOnOpenChange} {...drawerProps}>
        {content}
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={rootOnOpenChange} {...popoverProps}>
      {content}
    </Popover>
  );
};

export const PopoverDrawerTrigger = ({
  children,
  ...props
}: React.ComponentProps<typeof PopoverTrigger>) => {
  const { type } = usePopoverDrawerContext();

  if (type === "drawer") {
    return (
      <DrawerTrigger {...(props as React.ComponentProps<typeof DrawerTrigger>)}>
        {children}
      </DrawerTrigger>
    );
  }

  return <PopoverTrigger {...props}>{children}</PopoverTrigger>;
};

type PopoverDrawerContentProps = React.ComponentProps<typeof PopoverContent> & {
  drawerBodyClassName?: string;
  drawerContentProps?: Omit<
    React.ComponentProps<typeof DrawerContent>,
    "children" | "className" | "bodyClassName"
  >;
  drawerDescription?: string;
};

export const PopoverDrawerContent = ({
  align,
  alignOffset,
  anchor,
  children,
  className,
  drawerBodyClassName,
  drawerContentProps,
  drawerDescription,
  keepMounted,
  onCloseAutoFocus,
  onEscapeKeyDown,
  onInteractOutside,
  side,
  sideOffset,
  ...props
}: PopoverDrawerContentProps) => {
  const { title, type } = usePopoverDrawerContext();

  if (type === "drawer") {
    return (
      <DrawerContent
        bodyClassName={cn(drawerBodyClassName, className)}
        {...drawerContentProps}
      >
        <DrawerTitle className="sr-only">{title}</DrawerTitle>
        <DrawerDescription className="sr-only">
          {drawerDescription ?? title}
        </DrawerDescription>
        {children}
      </DrawerContent>
    );
  }

  return (
    <PopoverContent
      align={align}
      alignOffset={alignOffset}
      anchor={anchor}
      className={className}
      keepMounted={keepMounted}
      onCloseAutoFocus={onCloseAutoFocus}
      onEscapeKeyDown={onEscapeKeyDown}
      onInteractOutside={onInteractOutside}
      side={side}
      sideOffset={sideOffset}
      {...props}
    >
      {children}
    </PopoverContent>
  );
};
